import { fetchToken } from '@agissc/ktag-map';
import { useQuery } from '@tanstack/react-query';
import { arcgisToGeoJSON } from '@terraformer/arcgis';

export interface CodeValue {
  codeValue: string;
  meaning: string;
}

const fetchAttributes = async (url: string, isPublic: boolean) => {
  const maxQueryCount = 1000;
  const headers = new Headers();
  if (!isPublic) {
    const token = await fetchToken();
    headers.set('Authorization', `Bearer ${token}`);
  }

  const countUrlParams = {
    returnIdsOnly: 'true',
    returnCountOnly: 'true',
    returnGeometry: 'false',
    outFields: '*',
    f: 'json',
  };

  const responseCount = await fetch(
    `${url}&${new URLSearchParams(countUrlParams).toString()}`,
    {
      method: 'GET',
      headers,
    },
  );

  const value = await responseCount.json();
  const queryCount = value.count;

  if (queryCount > maxQueryCount) {
    throw new Error('maxQueryCountError', {
      cause: {
        queryCount,
        maxQueryCount,
      },
    });
  } else if (queryCount === 0) {
    throw new Error('queryCountZeroError');
  }

  const attributesUrlParams = {
    returnGeometry: 'true',
    outFields: '*',
    outSR: '2056',
    f: 'json',
  };

  const responseAttributes = await fetch(
    `${url}&${new URLSearchParams(attributesUrlParams).toString()}`,
    {
      method: 'GET',
      headers,
    },
  );

  const json = await responseAttributes.json();

  if (json.error) {
    throw new Error(json.error.message);
  }

  const oidField = json.fields?.find(
    (field: any) => field.type === 'esriFieldTypeOID',
  )?.name;

  const geojson: any = arcgisToGeoJSON(json, { idAttribute: oidField } as any);
  geojson.displayFieldName = json.displayFieldName;
  return geojson;
};

export const useAttributesQuery = (url: string | undefined, isPublic: boolean) => {
  return useQuery<any>({
    queryKey: ['count', url],
    enabled: !!url,
    queryFn: () => {
      return fetchAttributes(url!, isPublic);
    },
    gcTime: 3600000,
    staleTime: Infinity,
    retry: false,
  });
};
